import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trophy, Volume2, VolumeX, User, ArrowRight, RefreshCcw, ScrollText, Sparkles, CheckCircle2, XCircle, Share2, ChevronDown, ChevronUp, LogOut } from 'lucide-react';
import { quizBankQuestions, type QuizBankSection } from '../data/quizBank';

interface PKQuestion {
  id: number;
  sourceId: string;
  section: QuizBankSection;
  category: string;
  title: string;
  options: string[];
  answer: number;
  explanation: string;
}

interface KnowledgePKProps {
  onBack: () => void;
  intro?: string;
}

type GameState = 'home' | 'playing' | 'result';

const SCORE_PER_CORRECT = 10;
const ROUND_SIZE = 50;
const STORAGE_KEYS = {
  totalScore: 'qimen-knowledge-pk-total-score',
  seenQuestionIds: 'qimen-knowledge-pk-seen-question-ids',
} as const;

const QUESTION_BANK: PKQuestion[] = quizBankQuestions
  .map((q, idx) => {
    const options = Array.isArray(q.options) ? q.options.slice(0, 4).map((item) => String(item || '').trim()) : [];
    const answer = Array.isArray(q.correctAnswers)
      ? q.correctAnswers.find((n) => Number.isInteger(n) && Number(n) >= 0 && Number(n) <= 3)
      : undefined;

    return {
      id: idx + 1,
      sourceId: String(q.id || idx + 1),
      section: q.section,
      category: String(q.category || '未分类').trim() || '未分类',
      title: String(q.text || '').trim(),
      options,
      answer: typeof answer === 'number' ? answer : -1,
      explanation: String(q.explanation || '暂无解析').trim(),
    };
  })
  .filter((q) => q.title && q.options.length === 4 && q.answer >= 0);

const LEADERBOARD_DATA = [
  { id: 1, name: '道友青松', score: 120 },
  { id: 2, name: '学员小满', score: 90 },
  { id: 3, name: '匿名访客A', score: 80 },
  { id: 4, name: '匿名访客B', score: 70 },
  { id: 5, name: '练习生阿九', score: 50 },
  { id: 6, name: '问学新手', score: 40 },
  { id: 7, name: '晨读用户', score: 40 },
  { id: 8, name: '夜修用户', score: 30 },
  { id: 9, name: '体验用户C', score: 30 },
  { id: 10, name: '体验用户D', score: 20 },
];

const shuffleArray = <T,>(items: T[]): T[] => {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const readStoredNumber = (key: string): number => {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(key);
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

const readStoredStringArray = (key: string): string[] => {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const buildQuestionQueue = (
  questions: PKQuestion[],
  seenIds: Set<string>,
  roundSize: number,
): { queue: PKQuestion[]; resetCycle: boolean } => {
  const unseenPool = questions.filter((question) => !seenIds.has(question.sourceId));
  const pool = unseenPool.length > 0 ? unseenPool : questions;
  const resetCycle = unseenPool.length === 0 && questions.length > 0;

  if (pool.length === 0) {
    return { queue: [], resetCycle };
  }

  const buckets = new Map<string, PKQuestion[]>();
  shuffleArray(pool).forEach((question) => {
    const bucketKey = `${question.section}::${question.category}`;
    const current = buckets.get(bucketKey) || [];
    current.push(question);
    buckets.set(bucketKey, current);
  });

  const queue: PKQuestion[] = [];
  const targetCount = Math.min(roundSize, pool.length);
  let lastCategory = '';
  let lastSection = '';

  while (queue.length < targetCount) {
    const candidates = Array.from(buckets.entries()).filter(([, items]) => items.length > 0);
    if (candidates.length === 0) break;

    const rankedCandidates = shuffleArray(candidates).map(([bucketKey, items]) => {
      const [section, category] = bucketKey.split('::');
      let score = items.length * 10 + Math.random();

      if (category !== lastCategory) score += 100;
      if (section !== lastSection) score += 20;
      if (category === lastCategory) score -= 120;
      if (section === lastSection) score -= 15;

      return { bucketKey, score };
    });

    rankedCandidates.sort((a, b) => b.score - a.score);
    const nextBucket = buckets.get(rankedCandidates[0].bucketKey);
    const nextQuestion = nextBucket?.pop();
    if (!nextQuestion) break;

    queue.push(nextQuestion);
    lastCategory = nextQuestion.category;
    lastSection = nextQuestion.section;
  }

  return { queue, resetCycle };
};

export default function KnowledgePK({ onBack }: KnowledgePKProps) {
  const [gameState, setGameState] = useState<GameState>('home');
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(() => readStoredNumber(STORAGE_KEYS.totalScore));
  const [seenQuestionIds, setSeenQuestionIds] = useState<string[]>(() => readStoredStringArray(STORAGE_KEYS.seenQuestionIds));
  const [questionQueue, setQuestionQueue] = useState<PKQuestion[]>([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [isLeaderboardExpanded, setIsLeaderboardExpanded] = useState(false);

  const [sfxEnabled, setSfxEnabled] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const questionBank = useMemo(() => QUESTION_BANK, []);
  const seenQuestionIdSet = useMemo(() => new Set(seenQuestionIds), [seenQuestionIds]);
  const currentQuestion = questionQueue[currentQIndex] || null;
  const totalQuestionCount = questionBank.length;
  const remainingQuestionCount = Math.max(totalQuestionCount - seenQuestionIds.length, 0);
  const upcomingQuestionCount = Math.min(ROUND_SIZE, remainingQuestionCount || totalQuestionCount);

  useEffect(() => {
    const validQuestionIds = new Set(questionBank.map((question) => question.sourceId));
    setSeenQuestionIds((prev) => prev.filter((id) => validQuestionIds.has(id)));
  }, [questionBank]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEYS.totalScore, String(totalScore));
  }, [totalScore]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEYS.seenQuestionIds, JSON.stringify(seenQuestionIds));
  }, [seenQuestionIds]);

  useEffect(() => {
    if (gameState !== 'playing' || !currentQuestion) return;
    if (seenQuestionIdSet.has(currentQuestion.sourceId)) return;
    setSeenQuestionIds((prev) => (prev.includes(currentQuestion.sourceId) ? prev : [...prev, currentQuestion.sourceId]));
  }, [currentQuestion, gameState, seenQuestionIdSet]);

  const getAudioContext = (): AudioContext => {
    if (!audioCtxRef.current) {
      const audioCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!audioCtor) throw new Error('WebAudio not supported');
      audioCtxRef.current = new audioCtor();
    }
    if (audioCtxRef.current.state === 'suspended') {
      void audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  };

  const playSound = (type: 'click' | 'correct' | 'wrong' | 'win') => {
    if (!sfxEnabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      const now = ctx.currentTime;
      if (type === 'click') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.exponentialRampToValueAtTime(300, now + 0.1);
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
      } else if (type === 'correct') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(523.25, now);
        osc.frequency.setValueAtTime(659.25, now + 0.1);
        osc.frequency.setValueAtTime(783.99, now + 0.2);
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.5);
        osc.start(now);
        osc.stop(now + 0.5);
      } else if (type === 'wrong') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(80, now + 0.3);
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.3);
        osc.start(now);
        osc.stop(now + 0.3);
      } else if (type === 'win') {
        osc.type = 'square';
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.setValueAtTime(554.37, now + 0.1);
        osc.frequency.setValueAtTime(659.25, now + 0.2);
        osc.frequency.setValueAtTime(880, now + 0.3);
        gainNode.gain.setValueAtTime(0.2, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 1);
        osc.start(now);
        osc.stop(now + 1);
      }
    } catch (e) {
      console.log('Audio play failed', e);
    }
  };

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const handleAnswer = (index: number) => {
    if (showExplanation || !currentQuestion) return;
    setSelectedOption(index);
    const isCorrect = index === currentQuestion.answer;

    if (isCorrect) {
      setScore((prev) => prev + SCORE_PER_CORRECT);
      setTotalScore((prev) => prev + SCORE_PER_CORRECT);
      playSound('correct');
    } else {
      playSound('wrong');
    }

    setShowExplanation(true);
  };

  const handleNext = () => {
    playSound('click');
    if (currentQIndex < questionQueue.length - 1) {
      setCurrentQIndex((prev) => prev + 1);
      setSelectedOption(null);
      setShowExplanation(false);
    } else {
      setGameState('result');
      setShowExplanation(false);
      setTimeout(() => playSound('win'), 300);
    }
  };

  const startGame = () => {
    playSound('click');

    const { queue, resetCycle } = buildQuestionQueue(questionBank, seenQuestionIdSet, ROUND_SIZE);
    if (queue.length === 0) {
      showToast('题库准备中，请稍后再试。');
      return;
    }

    if (resetCycle) {
      setSeenQuestionIds([]);
      showToast('题库已轮完，已开启新一轮随机题目。');
    }

    setQuestionQueue(queue);
    setScore(0);
    setCurrentQIndex(0);
    setSelectedOption(null);
    setShowExplanation(false);
    setGameState('playing');
  };

  const goHome = () => {
    playSound('click');
    setGameState('home');
  };

  const quitGame = () => {
    playSound('click');
    const settledScore = score;
    setGameState('home');
    setCurrentQIndex(0);
    setSelectedOption(null);
    setShowExplanation(false);
    setQuestionQueue([]);
    setScore(0);

    if (settledScore > 0) {
      showToast(`本局已保留 ${settledScore} 分，累计积分已生效。`);
    } else {
      showToast('已退出比赛，已出题目不会重复出现。');
    }
  };

  const handleShare = () => {
    playSound('click');
    const textArea = document.createElement('textarea');
    textArea.value = window.location.href;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      showToast('页面链接已复制，快去邀请道友吧！');
    } catch {
      showToast('复制失败，请手动复制链接。');
    }
    document.body.removeChild(textArea);
  };

  const renderHome = () => {
    const displayedBoard = isLeaderboardExpanded ? LEADERBOARD_DATA : LEADERBOARD_DATA.slice(0, 7);

    return (
      <div className="flex flex-col h-full w-full relative z-10 p-5">
        <div className="flex justify-between items-center bg-white/30 backdrop-blur-xl p-2.5 rounded-2xl border border-white/60 shadow-[0_4px_16px_rgba(139,107,74,0.1)]">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#dccaae] to-[#b88c5d] rounded-full flex items-center justify-center text-white shadow-inner border border-white/80 relative">
              <User size={20} />
            </div>
            <div>
              <div className="text-xs font-bold text-[#4a3624]">神秘访客</div>
              <div className="text-[10px] text-[#7c5e42] font-medium">累计得分: <span className="text-[#b88c5d] font-bold">{totalScore}</span></div>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleShare}
              className="p-1.5 bg-white/40 border border-white/60 rounded-full hover:bg-white/60 transition text-[#8c6540] shadow-sm">
              <Share2 size={16} />
            </button>
            <button
              onClick={() => { setSfxEnabled(!sfxEnabled); if (!sfxEnabled) { getAudioContext(); playSound('click'); } }}
              className={`p-1.5 border border-white/60 rounded-full transition shadow-sm ${sfxEnabled ? 'bg-[#f2dfc7] text-[#b88c5d]' : 'bg-white/40 text-[#a39481]'}`}>
              {sfxEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
          </div>
        </div>

        <div className="mt-6 mb-4 text-center relative shrink-0">
          <h1 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-b from-[#5c3a21] to-[#2a1a10] tracking-widest font-serif drop-shadow-md relative z-10">
            奇门知识大PK
          </h1>
          <div className="flex justify-center items-center mt-2 space-x-2 opacity-80">
            <div className="h-px w-8 bg-gradient-to-r from-transparent to-[#b88c5d]"></div>
            <Sparkles className="text-[#b88c5d]" size={14} />
            <div className="h-px w-8 bg-gradient-to-l from-transparent to-[#b88c5d]"></div>
          </div>
        </div>

        <div className="flex-1 bg-white/20 backdrop-blur-2xl border border-white/60 rounded-[2rem] p-4 shadow-[0_8px_32px_rgba(107,74,46,0.15)] flex flex-col relative overflow-hidden transition-all duration-500 mb-3">
          <div className="absolute -right-4 -bottom-4 text-white/30 pointer-events-none mix-blend-overlay">
            <Trophy size={140} />
          </div>

          <div className="flex items-center justify-center space-x-2 text-[#8c6540] mb-3 relative z-10 shrink-0">
            <Trophy size={16} />
            <h2 className="font-bold text-base tracking-widest text-[#4a3624] font-serif">琅琊榜</h2>
            <Trophy size={16} />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 relative z-10 pr-1 custom-scrollbar">
            {displayedBoard.map((user, index) => (
              <div key={user.id} className="flex justify-between items-center p-2.5 bg-white/40 backdrop-blur-md rounded-xl border border-white/50 shadow-sm hover:bg-white/60 transition duration-300">
                <div className="flex items-center space-x-3">
                  <div className={`font-black italic w-5 text-center text-sm drop-shadow-sm
                    ${index === 0 ? 'text-[#cfa46e]' :
                      index === 1 ? 'text-[#9ba4a8]' :
                        index === 2 ? 'text-[#c28d68]' : 'text-[#a39481]'}`}>
                    {index + 1}
                  </div>
                  <div className="font-medium text-xs text-[#4a3624] font-serif">{user.name}</div>
                </div>
                <div className="text-[#a67c4d] font-bold font-mono text-xs">{user.score} <span className="text-[10px] text-[#a89882] font-sans">分</span></div>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-2 border-t border-white/30 relative z-10 shrink-0">
            <button
              onClick={() => setIsLeaderboardExpanded(!isLeaderboardExpanded)}
              className="w-full flex items-center justify-center py-1.5 text-[11px] text-[#8c6540] hover:text-[#5c3a21] transition-colors font-medium tracking-widest"
            >
              {isLeaderboardExpanded ? (
                <><ChevronUp size={14} className="mr-1" /> 收起排名</>
              ) : (
                <><ChevronDown size={14} className="mr-1" /> 查看更多排名</>
              )}
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center relative z-10 space-y-3 shrink-0 mb-2">
          <div className="w-full px-4 py-2 bg-white/30 backdrop-blur-md rounded-2xl border border-white/50 text-center shadow-sm">
            <div className="flex flex-col space-y-1">
              <p className="text-[#5c3a21] text-xs font-serif font-bold tracking-widest flex justify-center items-center">
                <Sparkles size={10} className="mr-1.5 text-[#b88c5d]" />
                比赛规则
                <Sparkles size={10} className="ml-1.5 text-[#b88c5d]" />
              </p>
              <p className="text-[#7c5e42] text-[11px] font-medium tracking-wide">
                答对一题立即加 {SCORE_PER_CORRECT} 分，中途退出也保留积分。
              </p>
              <p className="text-[#7c5e42] text-[11px] font-medium tracking-wide">
                题目按全题库打散，已出过的题下次不会重复。当前待抽题目 {upcomingQuestionCount} 道。
              </p>
            </div>
          </div>

          <button
            onClick={startGame}
            className="w-3/4 py-3.5 bg-gradient-to-r from-[#cfa46e]/90 to-[#b08454]/90 backdrop-blur-md hover:from-[#b08454] hover:to-[#966b3f] active:scale-95 transition-all duration-300 rounded-[1.2rem] text-[#fdf9f1] font-bold text-base shadow-[0_8px_20px_rgba(184,140,93,0.3)] border border-white/30 flex justify-center items-center space-x-2"
          >
            <span className="tracking-widest">开始答题</span>
            <ArrowRight size={18} className="animate-pulse" />
          </button>
        </div>
      </div>
    );
  };

  const renderPlaying = () => {
    if (!currentQuestion) {
      return (
        <div className="flex flex-col h-full w-full relative z-10 p-5 items-center justify-center">
          <div className="text-sm text-[#5c3a21] font-serif">题目加载中...</div>
        </div>
      );
    }

    const progressPercent = ((currentQIndex + 1) / questionQueue.length) * 100;

    return (
      <div className="flex flex-col h-full w-full relative z-10 p-5">
        <div className="flex justify-between items-center mb-3 relative z-10 shrink-0">
          <button
            onClick={quitGame}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/30 backdrop-blur-md border border-white/60 rounded-full text-[#8c6540] hover:bg-white/60 hover:text-[#5c3a21] transition shadow-sm text-[11px] font-bold tracking-widest"
          >
            <LogOut size={12} />
            <span>退出比赛</span>
          </button>
        </div>

        <div className="flex justify-between items-center mb-5 bg-white/30 backdrop-blur-xl p-3.5 rounded-2xl shadow-[0_4px_16px_rgba(139,107,74,0.1)] border border-white/60 shrink-0">
          <div className="flex flex-col flex-1 mr-5">
            <div className="flex justify-between text-[10px] font-bold mb-1.5">
              <span className="text-[#8c6540] tracking-wider">答题进度</span>
              <span className="text-[#b88c5d] font-bold">{currentQIndex + 1} / {questionQueue.length}</span>
            </div>
            <div className="w-full bg-black/5 h-2 rounded-full relative overflow-hidden shadow-inner border border-black/5">
              <div
                style={{ width: `${progressPercent}%` }}
                className="bg-gradient-to-r from-[#d2ae82] to-[#b88c5d] h-full rounded-full transition-all duration-500 ease-out shadow-sm"
              ></div>
            </div>
          </div>
          <div className="text-right pl-4 border-l border-[#dccaae]/50">
            <span className="text-[10px] text-[#8c6540] tracking-wider block mb-0.5">当前得分</span>
            <div className="text-xl font-black text-[#4a3624] font-mono drop-shadow-sm">
              {score}
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-2xl rounded-[2rem] p-5 shadow-[0_8px_32px_rgba(107,74,46,0.15)] border border-white/60 mb-3 flex-1 flex flex-col overflow-y-auto custom-scrollbar">
          <div className="flex items-center justify-between text-[#8c6540] mb-4 border-b border-white/40 pb-2 shrink-0">
            <div className="flex items-center space-x-2">
              <ScrollText size={16} />
              <span className="font-bold font-serif text-sm tracking-widest text-[#4a3624]">第 {currentQIndex + 1} 题</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] text-[#8c6540] bg-white/40 px-2 py-1 rounded-full border border-white/50">
                {currentQuestion.category}
              </span>
              <Sparkles size={14} className="text-[#b88c5d]" />
            </div>
          </div>

          <h2 className="text-base sm:text-lg font-bold text-[#3a2515] leading-relaxed mb-5 shrink-0 font-serif drop-shadow-sm">
            {currentQuestion.title}
          </h2>

          <div className="space-y-2.5 shrink-0">
            {currentQuestion.options.map((opt, idx) => {
              let btnClass = 'bg-white/40 border-white/50 text-[#4a3624] hover:bg-white/60';
              let icon: React.ReactNode = null;

              if (showExplanation) {
                if (idx === currentQuestion.answer) {
                  btnClass = 'bg-emerald-100/60 border-emerald-300 text-emerald-900 shadow-md backdrop-blur-md';
                  icon = <CheckCircle2 className="text-emerald-600 ml-auto" size={16} />;
                } else if (idx === selectedOption) {
                  btnClass = 'bg-red-100/60 border-red-300 text-red-900 shadow-md backdrop-blur-md';
                  icon = <XCircle className="text-red-500 ml-auto" size={16} />;
                } else {
                  btnClass = 'bg-white/20 border-white/30 text-[#bda78e]';
                }
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  disabled={showExplanation}
                  className={`w-full text-left p-3 rounded-xl border transition-all duration-300 text-sm font-medium flex items-center shadow-sm ${btnClass}`}
                >
                  <span className={`inline-block w-7 font-black mr-1 drop-shadow-sm ${showExplanation && idx === currentQuestion.answer ? 'text-emerald-600' : 'text-[#a67c4d]'}`}>
                    {['A', 'B', 'C', 'D'][idx]}.
                  </span>
                  <span className="font-serif">{opt}</span>
                  {icon}
                </button>
              );
            })}
          </div>

          {showExplanation && (
            <div className="mt-4 animate-fade-in-up shrink-0 pb-2">
              <div className="p-3 bg-white/50 backdrop-blur-md border border-white/60 rounded-xl relative overflow-hidden shadow-inner">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#b88c5d] to-[#d2ae82]"></div>
                <h4 className="text-[11px] font-bold text-[#a67c4d] mb-1.5 flex items-center tracking-widest">
                  <Sparkles size={12} className="mr-1" /> 天机解析
                </h4>
                <p className="text-[#5c4431] text-[11px] leading-relaxed font-serif">
                  {currentQuestion.explanation}
                </p>
              </div>

              <button
                onClick={handleNext}
                className="mt-4 w-3/4 mx-auto block py-3 bg-gradient-to-r from-[#cfa46e]/90 to-[#b08454]/90 backdrop-blur-md hover:from-[#b08454] hover:to-[#966b3f] text-[#fdf9f1] font-bold text-sm rounded-[1rem] shadow-[0_4px_12px_rgba(184,140,93,0.3)] transition transform hover:scale-[1.02] active:scale-95 border border-white/30 tracking-widest"
              >
                {currentQIndex < questionQueue.length - 1 ? '下一题' : '查看比赛结果'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResult = () => (
    <div className="flex flex-col h-full w-full relative z-10 p-5 items-center justify-center">
      <div className="bg-white/20 backdrop-blur-2xl rounded-[2.5rem] p-8 shadow-[0_16px_40px_rgba(107,74,46,0.2)] border border-white/60 w-full max-w-sm text-center relative overflow-hidden">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/40 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col items-center">
          <Trophy size={56} className="mx-auto text-[#cfa46e] mb-4 drop-shadow-md filter brightness-110" />
          <h2 className="text-2xl font-black text-[#4a3624] mb-4 font-serif tracking-widest drop-shadow-sm">比赛结算</h2>

          <div className="bg-white/40 backdrop-blur-md border border-white/60 py-6 px-10 rounded-3xl inline-block mb-10 shadow-sm relative">
            <span className="text-[#8c6540] text-xs block mb-2 tracking-widest font-bold">本次总分</span>
            <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-b from-[#cfa46e] to-[#966b3f] drop-shadow-md font-mono leading-none">
              {score}
            </div>
          </div>

          <div className="space-y-3 w-4/5 mx-auto">
            <button
              onClick={startGame}
              className="w-full py-3.5 bg-gradient-to-r from-[#cfa46e]/90 to-[#b08454]/90 backdrop-blur-md hover:from-[#b08454] hover:to-[#966b3f] transition transform active:scale-95 rounded-[1.2rem] text-[#fdf9f1] font-bold text-sm shadow-[0_6px_16px_rgba(184,140,93,0.3)] flex justify-center items-center space-x-2 border border-white/30"
            >
              <RefreshCcw size={16} />
              <span className="tracking-widest">再次挑战</span>
            </button>

            <button
              onClick={goHome}
              className="w-full py-3.5 bg-white/40 hover:bg-white/60 backdrop-blur-md text-[#7c5e42] border border-white/60 transition transform active:scale-95 rounded-[1.2rem] font-bold text-sm shadow-sm tracking-widest"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-[#f0e8dc] flex justify-center items-stretch sm:items-center font-sans p-0 sm:p-4 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-[#e6d0b3] rounded-full mix-blend-multiply filter blur-[100px] opacity-60 pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-[#dccaae] rounded-full mix-blend-multiply filter blur-[100px] opacity-60 pointer-events-none"></div>
      <div className="w-full max-w-none sm:max-w-md h-[100dvh] max-h-none sm:max-h-[900px] bg-gradient-to-br from-[#f8f1e6] to-[#eae0d1] relative overflow-hidden shadow-none sm:shadow-[0_20px_60px_rgba(107,74,46,0.3)] rounded-none sm:rounded-[2.5rem] border-0 sm:border-[8px] sm:border-white/50 backdrop-blur-3xl">
        <div className="absolute top-[20%] left-[-20%] w-64 h-64 bg-white rounded-full filter blur-[80px] opacity-50 pointer-events-none"></div>
        <div className="absolute bottom-[20%] right-[-20%] w-64 h-64 bg-[#f2dfc7] rounded-full filter blur-[80px] opacity-60 pointer-events-none"></div>

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in-up {
            animation: fadeInUp 0.4s ease-out forwards;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(184, 140, 93, 0.3);
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(184, 140, 93, 0.5);
          }
        `}</style>

        {gameState === 'home' && renderHome()}
        {gameState === 'playing' && renderPlaying()}
        {gameState === 'result' && renderResult()}

        {toastMsg && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-white/80 backdrop-blur-xl border border-white/60 text-[#4a3624] px-5 py-2.5 rounded-full shadow-[0_8px_16px_rgba(107,74,46,0.15)] text-[11px] font-bold tracking-widest z-50 animate-fade-in-up flex items-center space-x-2">
            <CheckCircle2 size={14} className="text-[#cfa46e]" />
            <span>{toastMsg}</span>
          </div>
        )}
      </div>
    </div>
  );
}
